import FolderItemState from 'filestate/FolderItemState';
import {messageRouter} from 'notebook/realtime/DocumentUpdateMessageHandler';
import getRealtimeChannel from 'realtime/getRealtimeChannel';
import RealtimeChannel, {RealtimeChannelCapability, RealtimeError} from 'realtime/RealtimeChannel';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import {getStore} from 'redux-stores/Store';
import EntityStore from 'stores/EntityStore';
import * as logging from 'util/Logging';
import {Optional} from 'util/Types';

import {getResourceIdForRealtime} from '../../buffers/PresenceManager';
import {ServerDocumentEditorLock} from './DocumentEditorLockConstants';
import {fetchLock} from './DocumentEditorLockDAO';
import {documentEditorLockClear, documentEditorLockUpdate} from './DocumentEditorLockReduxActions';

export const MAX_DOCUMENT_CONNECTIONS = 20;

interface DocumentConnection {
  fileId: string;
  realtimeChannel: RealtimeChannel;
}

/**
 * Manages the realtime connections for the current document.
 *
 * This is a realtime channel consumer for opened document. It's important to note that this consumer does
 * not use the traditional React-hooks approach as it is mounted in our Backbone app before the React portals.
 *
 * Consider using the React-hooks approach before extending this manager or patterning off it.
 * See: client/realtime/useRealtimeChannelSubscription.ts
 */
export default class DocumentUpdatesManager {
  activeDocument: Optional<DocumentConnection>;
  allDocumentConnections: Array<DocumentConnection>;

  constructor() {
    this.allDocumentConnections = [];
  }

  changeActiveDocument(fileRef: FolderItemState | string | null): void {
    if (EntityStore.currentUser() == null) {
      return;
    }
    this.markInactive();
    const fileId = typeof fileRef === 'string' ? fileRef : getResourceIdForRealtime(fileRef);
    if (!fileId) {
      return;
    }

    this.allDocumentConnections
      .filter((documentConnection) => documentConnection.fileId === fileId)
      .forEach((documentConnection) => {
        documentConnection.realtimeChannel.stop();
      });
    this.allDocumentConnections = this.allDocumentConnections.filter(
      (documentConnection) => documentConnection.fileId !== fileId
    );

    getRealtimeChannel(getRealtimeChannelNameForApiId(fileId), [
      RealtimeChannelCapability.SUBSCRIBE,
      RealtimeChannelCapability.PRESENCE,
    ])
      .then((realtimeChannel) => {
        this.activeDocument = {
          fileId,
          realtimeChannel,
        };
        realtimeChannel.subscribe(messageRouter(fileId, 'active'));
        // Make sure we have the current lock state
        fetchAndUpdateFileLockState(fileId);
      })
      .catch((err: Error) => {
        if (err instanceof RealtimeError) {
          logging.warn(`error connecting to realtime channel`, {extra: {err}});
          return;
        }

        throw err;
      });
  }

  markInactive(): void {
    if (!this.activeDocument) {
      return;
    }

    if (this.allDocumentConnections.length >= MAX_DOCUMENT_CONNECTIONS) {
      this.allDocumentConnections[0].realtimeChannel.stop();
      this.allDocumentConnections.shift();
    }

    this.activeDocument.realtimeChannel.unsubscribe();
    this.activeDocument.realtimeChannel.subscribe(messageRouter(this.activeDocument.fileId, 'inactive'));
    this.allDocumentConnections.push(this.activeDocument);
    this.activeDocument = null;
  }
}

export async function fetchAndUpdateFileLockState(
  fileId: string,
  serverLock?: ServerDocumentEditorLock
): Promise<void> {
  let clientLock: ServerDocumentEditorLock | undefined | null = serverLock;
  if (!clientLock) {
    clientLock = await fetchLock(fileId);
  }
  // If there is a client lock and it isn't expired, update it in the store. Otherwise, clear it.
  if (clientLock && clientLock.msUntilExpiration > 0) {
    getStore().dispatch(
      documentEditorLockUpdate(fileId, clientLock.userId, clientLock.clientId, clientLock.msUntilExpiration)
    );
  } else {
    getStore().dispatch(documentEditorLockClear(fileId, true));
  }
}
